import { supabase } from '@/lib/supabaseClient'

// Interface for Product
export interface Product {
  id: string;
  title: string;
  price?: number;
  compare_at_price?: number;
  image_link?: string;
  description?: string;
  category?: string;
  stock?: number | string;
  vendor?: string;
  created_at?: string;
  updated_at?: string;
  organization_id?: string;
}

// Get all products with optional filtering
export async function getProducts(organizationId: string | null = null) {
  console.log(`[ProductService] getProducts - Starting with organizationId: ${organizationId}`);
  const startTime = performance.now();
  try {
    // If no organizationId provided, get the current user's organization
    if (!organizationId) {
      console.log('[ProductService] No organizationId provided, getting current user');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.warn('[ProductService] User not authenticated - returning sample data');
        return { products: getSampleProducts(), error: null };
      }
      
      // Get the user's organization ID
      console.log(`[ProductService] Found authenticated user: ${user.id}`);
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .single();
      
      if (profileError || !userProfile?.org_id) {
        console.warn('[ProductService] User has no organization - returning sample data', profileError);
        return { products: getSampleProducts(), error: null };
      }
      
      organizationId = userProfile.org_id as string;
      console.log(`[ProductService] Using organization ID: ${organizationId}`);
    }
    
    // Fetch products from the database
    console.log(`[ProductService] Fetching products for organization: ${organizationId}`);
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('[ProductService] Error fetching products:', error);
      return { products: [], error: error.message };
    }
    
    if (!data || data.length === 0) {
      console.warn('[ProductService] No products found in database');
      return { products: [], error: null };
    }
    
    console.log(`[ProductService] Successfully fetched ${data.length} products`);
    const endTime = performance.now();
    console.log(`[ProductService] getProducts completed in ${(endTime - startTime).toFixed(2)}ms`);
    return { products: data, error: null };
  } catch (error) {
    console.error('[ProductService] Exception in getProducts:', error);
    return { 
      products: getSampleProducts(), 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

// Get top selling products - used for the dashboard
export async function getTopSellingProducts(organizationId: string | null = null, period = 'week', limit = 10) {
  const startTime = performance.now();
  console.log(`[ProductService] getTopSellingProducts - Starting - period: ${period}, limit: ${limit}`);
  try {
    // If no organizationId provided, get the current user's organization
    if (!organizationId) {
      console.log('[ProductService] No organizationId provided, getting current user');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.warn('[ProductService] User not authenticated - returning empty array');
        return { products: [], error: 'User not authenticated' };
      }
      
      // Get the user's organization ID
      console.log('[ProductService] Getting user organization ID');
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .single();
      
      if (profileError || !userProfile?.org_id) {
        console.warn('[ProductService] User has no organization - returning empty array', profileError);
        return { products: [], error: 'User has no organization' };
      }
      
      organizationId = userProfile.org_id as string;
      console.log(`[ProductService] Using organization ID: ${organizationId}`);
    }
    
    // Calculate date range based on period
    console.log(`[ProductService] Calculating date range for period: ${period}`);
    const dateFilter = new Date();
    switch(period) {
      case 'day':
        dateFilter.setDate(dateFilter.getDate() - 1);
        break;
      case 'week':
        dateFilter.setDate(dateFilter.getDate() - 7);
        break;
      case 'month':
        dateFilter.setMonth(dateFilter.getMonth() - 1);
        break;
      case 'year':
        dateFilter.setFullYear(dateFilter.getFullYear() - 1);
        break;
      default:
        dateFilter.setDate(dateFilter.getDate() - 7); // Default to week
    }
    
    // Format date for SQL
    const dateStr = dateFilter.toISOString();
    console.log(`[ProductService] Filtering orders from date: ${dateStr}`);
    
    // First fetch recent orders
    const orderStartTime = performance.now();
    console.log(`[ProductService] Fetching recent orders for org: ${organizationId}, since: ${dateStr}`);
    const { data: orders, error: orderError } = await supabase
      .from('orders')
      .select('id, products')
      .eq('organization_id', organizationId)
      .gt('created_at', dateStr);
    
    if (orderError) {
      console.error('[ProductService] Error fetching orders for top products:', orderError);
      return { products: [], error: orderError.message };
    }
    
    const orderEndTime = performance.now();
    console.log(`[ProductService] Retrieved ${orders?.length || 0} orders in ${(orderEndTime - orderStartTime).toFixed(2)}ms`);
    
    // Check if we have any orders
    if (!orders || orders.length === 0) {
      console.warn(`[ProductService] No orders found for period: ${period}`);
      return { products: [], error: null };
    }
    
    // Count product occurrences in orders with quantities
    console.log('[ProductService] Processing orders to count product occurrences');
    const productCounts: Record<string, { count: number, quantity: number }> = {};
    
    orders?.forEach((order, index) => {
      console.log(`[ProductService] Processing order ${index + 1}/${orders.length}, ID: ${order.id}`);
      let productIds = order.products;
      if (typeof productIds === 'string') {
        try {
          productIds = JSON.parse(productIds);
          // Add a type guard to make sure productIds is an array before accessing length
          if (Array.isArray(productIds)) {
            console.log(`[ProductService] Parsed products for order ${order.id}, found ${productIds.length} products`);
          } else {
            console.log(`[ProductService] Parsed products for order ${order.id}, but result is not an array: ${typeof productIds}`);
          }
        } catch(e) {
          console.warn(`[ProductService] Failed to parse products JSON for order ${order.id}:`, e);
          return;
        }
      }
      
      if (Array.isArray(productIds)) {
        console.log(`[ProductService] Processing ${productIds.length} products in order ${order.id}`);
        productIds.forEach((productItem, prodIndex) => {
          // Handle different possible structures of product data in orders
          let productId, quantity = 1;
          
          if (typeof productItem === 'object' && productItem !== null) {
            // Case: { id: "123", quantity: 2, ... }
            productId = productItem.id;
            quantity = productItem.quantity || 1;
            console.log(`[ProductService] Product ${prodIndex + 1} is an object: ID=${productId}, quantity=${quantity}`);
          } else {
            // Case: "123" (just the ID as a string)
            productId = productItem;
            console.log(`[ProductService] Product ${prodIndex + 1} is a string ID: ${productId}`);
          }
          
          if (!productId) {
            console.warn(`[ProductService] Invalid product ID in order ${order.id}, product index ${prodIndex}`);
            return;
          }
          
          if (!productCounts[productId]) {
            productCounts[productId] = { count: 0, quantity: 0 };
          }
          
          productCounts[productId].count += 1;
          productCounts[productId].quantity += Number(quantity);
        });
      } else {
        console.warn(`[ProductService] Products not an array for order ${order.id}, type: ${typeof productIds}`);
      }
    });
    
    console.log(`[ProductService] Product occurrence counts calculated for ${Object.keys(productCounts).length} unique products`);
    console.log('[ProductService] Top 5 products by count:', Object.entries(productCounts)
      .sort((a, b) => b[1].quantity - a[1].quantity)
      .slice(0, 5)
      .map(([id, stats]) => `ID: ${id.substring(0, 8)}..., count: ${stats.count}, quantity: ${stats.quantity}`));
    
    // Sort product IDs by total quantity sold
    console.log('[ProductService] Sorting products by total quantity sold');
    const topProductIds = Object.entries(productCounts)
      .sort((a, b) => b[1].quantity - a[1].quantity)
      .slice(0, limit)
      .map(entry => entry[0]);
    
    console.log(`[ProductService] Top ${topProductIds.length} product IDs selected`);
    
    if (topProductIds.length === 0) {
      console.warn('[ProductService] No top products found, returning empty array');
      return { products: [], error: null };
    }
    
    // Fetch the actual products
    const productFetchStart = performance.now();
    console.log(`[ProductService] Fetching product details for ${topProductIds.length} products`);
    const { data: products, error: productError } = await supabase
      .from('products')
      .select('*')
      .in('id', topProductIds);
    
    if (productError) {
      console.error('[ProductService] Error fetching top products:', productError);
      return { products: [], error: productError.message };
    }
    
    const productFetchEnd = performance.now();
    console.log(`[ProductService] Retrieved ${products?.length || 0} products in ${(productFetchEnd - productFetchStart).toFixed(2)}ms`);
    
    if (!products || products.length === 0) {
      console.warn('[ProductService] No products found in database, returning empty array');
      return { products: [], error: null };
    }
    
    // Sort products according to the topProductIds order
    console.log('[ProductService] Sorting products according to quantity ranking');
    const sortedProducts = topProductIds
      .map(id => {
        const product = products?.find(product => product.id === id);
        if (!product) {
          console.warn(`[ProductService] Product with ID ${id} not found in fetched products`);
        }
        return product;
      })
      .filter(Boolean) as unknown as Product[];
    
    console.log(`[ProductService] Sorted ${sortedProducts.length} products successfully`);
    
    // Add stats data to products for UI
    console.log('[ProductService] Adding sales statistics to product data');
    const productsWithStats = sortedProducts.map(product => {
      const productId = product.id;
      const stats = productCounts[productId] || { count: 0, quantity: 0 };
      console.log(`[ProductService] Product ${product.title} (${product.id.substring(0, 8)}): orders=${stats.count}, quantity=${stats.quantity}`);
      
      return {
        ...product,
        orderCount: stats.count, // Number of orders containing this product
        totalQuantity: stats.quantity, // Total quantity sold
        // Convert stock to 'Stock Out' if it's 0
        stock: product.stock === 0 ? 'Stock Out' : product.stock
      };
    });
    
    console.log('[ProductService] Product stats added successfully');
    console.log('[ProductService] Top selling products by quantity:', 
      productsWithStats.slice(0, 3).map(p => ({
        id: p.id.substring(0, 8), 
        title: p.title, 
        orderCount: (p as any).orderCount,
        totalQuantity: (p as any).totalQuantity
      })));
    
    const endTime = performance.now();
    console.log(`[ProductService] getTopSellingProducts completed in ${(endTime - startTime).toFixed(2)}ms`);
    
    return { products: productsWithStats, error: null };
  } catch (error) {
    console.error('[ProductService] Exception in getTopSellingProducts:', error);
    const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error(`[ProductService] Error details: ${errorMsg}`);
    return { 
      products: [], 
      error: errorMsg
    };
  }
}

// Sample products for fallback
function getSampleProducts(): Product[] {
  console.log('[ProductService] Generating sample products for fallback');
  // Import images directly
  const productImages = Array.from({ length: 5 }, (_, i) => {
    return new URL(`../assets/images/products/product${i + 1}.jpg`, import.meta.url).href;
  });

  return [
    {
      id: "1",
      title: "Smart Band",
      price: 35.00,
      stock: 750,
      image_link: productImages[0],
      vendor: "ACME Electronics",
      category: "Wearables",
      created_at: "2024-06-15T00:00:00Z"
    },
    {
      id: "2",
      title: "Headphone",
      price: 49.00,
      stock: 825,
      image_link: productImages[1],
      vendor: "Audio Pro",
      category: "Audio",
      created_at: "2024-06-14T00:00:00Z" 
    },
    {
      id: "3",
      title: "iPhone 15 Plus",
      price: 99.00,
      stock: "Stock Out",
      image_link: productImages[2],
      vendor: "Apple",
      category: "Phones",
      created_at: "2024-06-13T00:00:00Z"
    },
    {
      id: "4",
      title: "Bluetooth Speaker",
      price: 59.00,
      stock: 535,
      image_link: productImages[3],
      vendor: "Sound Wave",
      category: "Audio",
      created_at: "2024-06-12T00:00:00Z"
    },
    {
      id: "5",
      title: "Airbuds 2nd Gen",
      price: 79.00,
      stock: 460,
      image_link: productImages[4],
      vendor: "Sound Pro",
      category: "Audio",
      created_at: "2024-06-11T00:00:00Z"
    }
  ];
} 